import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AuthApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, KeyRound, Loader2, ShieldCheck, History, Clock } from "lucide-react";

export default function UserManager({ open, onOpenChange, currentUserId }) {
  const [users, setUsers] = useState(null);
  const [nu, setNu] = useState({ username: "", password: "", name: "", role: "user" });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [view, setView] = useState("users");
  const [logins, setLogins] = useState(null);

  const load = () => { setUsers(null); AuthApi.listUsers().then(setUsers).catch(() => setUsers([])); };
  useEffect(() => { if (open) { setView("users"); load(); } }, [open]);
  useEffect(() => {
    if (open && view === "logins") { setLogins(null); AuthApi.loginEvents(150).then(setLogins).catch(() => setLogins([])); }
  }, [open, view]);

  const fmtDateTime = (iso) => {
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return "—"; }
  };

  const create = async () => {
    if (!nu.username.trim() || !nu.password) { toast.error("Nome utente e password obbligatori"); return; }
    setSaving(true);
    try {
      await AuthApi.createUser({ ...nu, username: nu.username.trim(), name: nu.name.trim() });
      toast.success("Utente creato");
      setNu({ username: "", password: "", name: "", role: "user" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione utente"); }
    finally { setSaving(false); }
  };

  const doDelete = async (u) => {
    try { await AuthApi.deleteUser(u.id); toast.success("Utente eliminato"); setToDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
  };

  const doReset = async () => {
    if (!newPw) { toast.error("Inserisci la nuova password"); return; }
    try { await AuthApi.resetPassword(resetFor.id, newPw); toast.success(`Password di ${resetFor.name} aggiornata`); setResetFor(null); setNewPw(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore reset password"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="user-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" /> Gestione utenti
          </DialogTitle>
          <DialogDescription>Aggiungi o rimuovi operatori, reimposta le password e controlla gli accessi.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1 border-b border-border -mt-1">
          <button data-testid="tab-users" onClick={() => setView("users")}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${view === "users" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            Utenti
          </button>
          <button data-testid="tab-logins" onClick={() => setView("logins")}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${view === "logins" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <History className="h-3.5 w-3.5" /> Registro accessi
          </button>
        </div>

        {view === "logins" ? (
          <div className="space-y-1.5 max-h-80 overflow-auto" data-testid="login-events-list">
            {logins === null ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento...</div>
            ) : logins.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="login-events-empty">Nessun accesso registrato.</div>
            ) : logins.map((ev) => (
              <div key={ev.id} data-testid={`login-event-${ev.id}`} className="flex items-center gap-2 border border-border rounded-md px-3 py-2">
                <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                  {(ev.name || ev.username || "?").charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    {ev.name || ev.username}
                    {ev.role === "admin" && <span className="text-[10px] font-bold text-primary">ADMIN</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">@{ev.username}{ev.ip ? ` · ${ev.ip}` : ""}</div>
                </div>
                <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 shrink-0">
                  <Clock className="h-3 w-3" /> {fmtDateTime(ev.at)}
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className="space-y-3">
          <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
              <UserPlus className="h-3.5 w-3.5" /> Nuovo utente
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="new-user-username" value={nu.username} onChange={(e) => setNu({ ...nu, username: e.target.value })} placeholder="Nome utente *" className="h-8 text-sm" />
              <Input data-testid="new-user-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Nome visualizzato" className="h-8 text-sm" />
              <Input data-testid="new-user-password" type="text" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} placeholder="Password *" className="h-8 text-sm" />
              <Select value={nu.role} onValueChange={(v) => setNu({ ...nu, role: v })}>
                <SelectTrigger className="h-8 text-sm" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="user">Operatore</SelectItem>
                  <SelectItem value="admin">Amministratore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" className="h-8 w-full" disabled={saving} data-testid="create-user-btn" onClick={create}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiungi utente"}
            </Button>
          </div>

          <div className="space-y-1.5 max-h-64 overflow-auto" data-testid="users-list">
            {users === null ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento...</div>
            ) : users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.username}`} className="flex items-center gap-2 border border-border rounded-md px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    {u.name || u.username}
                    {u.role === "admin" && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-primary border border-primary/30 bg-primary/10 rounded px-1"><ShieldCheck className="h-3 w-3" /> ADMIN</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">@{u.username}</div>
                </div>
                <button data-testid={`reset-pw-${u.username}`} onClick={() => { setResetFor(u); setNewPw(""); }}
                  title="Reimposta password" className="text-muted-foreground hover:text-primary transition-colors"><KeyRound className="h-4 w-4" /></button>
                {u.id !== currentUserId && (
                  <button data-testid={`delete-user-${u.username}`} onClick={() => setToDelete(u)}
                    title="Elimina utente" className="text-muted-foreground hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
        )}
      </DialogContent>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'utente {toDelete?.name || toDelete?.username}?</AlertDialogTitle>
            <AlertDialogDescription>L'utente non potrà più accedere. L'operazione non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => doDelete(toDelete)}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Nuova password per {resetFor?.name || resetFor?.username}</AlertDialogTitle>
            <AlertDialogDescription>Imposta una nuova password per questo utente.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input data-testid="reset-pw-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nuova password" className="h-9" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-reset-pw">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-reset-pw" onClick={(e) => { e.preventDefault(); doReset(); }}>Salva password</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
